import type { InicioProduct, InicioShortcut } from "@orkestor/skin-platform";
import { routes as checkmeRoutes } from "@orkestor/skin-check-me/routes";
import { routes as pencilRoutes } from "@orkestor/skin-pencil-chief/routes";
import type { ReactNode } from "react";

/**
 * The ecosystem's product catalogue and the home's shortcuts.
 *
 * This is the one place that legitimately knows every product, so it is the
 * shell's — the same reason `homePath` is a prop on the Portada. It lived
 * inside Pencil Chief's skin before, which meant Pencil owned the description
 * of Check Me and Why Humans and the platform could not show its own home
 * without depending on a product.
 *
 * It is content, not UI logic: no component here, only the list of what the
 * shell composes. `stats` is the exception, and it is passed in rather than
 * built here so the node comes from the product's own skin.
 */
export function productCatalogue(
  pencilStats: ReactNode,
  /** Lo que la organización activa tiene contratado, o `undefined` si NO CONSTA.
   *  Se pasa desde fuera porque este fichero es contenido, no estado: quien sabe
   *  qué organización está activa es el shell. */
  contratados?: string[] | undefined,
): InicioProduct[] {
  /** `undefined` cuando no consta: la tarjeta no afirma lo que no se sabe. */
  const tiene = (producto: string): boolean | undefined =>
    contratados === undefined ? undefined : contratados.includes(producto);

  return [
    {
      name: "Pencil Chief",
      category: "Búsqueda de técnicos",
      description:
        "Publica ofertas, pencilea candidatos y confirma equipo sin salir de la agenda de la productora.",
      live: true,
      contratado: tiene("pencil_chief"),
      to: pencilRoutes.ofertas,
      stats: pencilStats,
    },
    {
      name: "Check Me",
      category: "Control de tiempo",
      description:
        "La jornada de cada producción: el equipo ficha por QR o desde la app, y producción revisa, corrige y aprueba el parte.",
      live: true,
      contratado: tiene("check_me"),
      to: checkmeRoutes.hoy,
    },
    {
      name: "Why Humans",
      category: "Casting de artistas",
      description:
        "Convocatorias, self-tapes y listas cortas para el reparto, con el mismo perfil de empresa.",
      live: false,
      contratado: tiene("why_humans"),
    },
  ];
}

/** The three shortcuts of frame 02. Only the one that creates something takes
    the accent — the yellow rule of `design-system-spec.md`. */
export const SHORTCUTS: InicioShortcut[] = [
  {
    icon: "plus",
    label: "Publicar oferta",
    hint: "Un puesto nuevo en una producción",
    to: pencilRoutes.ofertaNueva,
    primary: true,
  },
  {
    icon: "search",
    label: "Buscar técnicos",
    hint: "Por puesto, fechas y distancia",
    to: pencilRoutes.buscar,
  },
  {
    icon: "cal",
    label: "Ver la agenda",
    hint: "Firmes, opciones y huecos",
    to: pencilRoutes.agenda,
  },
];
